/**
 * errorCode-aware toast helper for `POST /reservations/batch-reserve` conflict
 * responses.
 *
 * Every batch-reserve call site previously showed the SAME generic
 * "already reserved" toast (`toasts.batchReserveConflict`) for ANY entry in
 * `response.conflicted[]`, regardless of that cell's real `errorCode` — a
 * cell that failed because of a stale offer version, a permission change, or
 * a validation error looked identical to a genuine double-booking race.
 *
 * `showBatchReserveConflictToasts` groups `conflicted[]` by its
 * errorCode-derived reason and shows one toast per distinct reason (with
 * that reason's own count), instead of a single blanket message. `keyPrefix`
 * lets each caller supply its own i18n namespace/prefix — every current call
 * site uses the `reservations` namespace's `toasts` prefix (the default),
 * but this stays parameterized instead of hardcoding that path.
 *
 * The feature-local `errorCodeToToastKey`/`handleReserve` in
 * `offers/availability-shared/availability-reservation-actions.ts` implement a
 * single-cell, non-batch `POST /reservations` create flow with two extra
 * codes (OPTION_EXTENSION_IMPOSSIBLE, AVAILABILITY_WINDOW_BLOCKED) that don't
 * apply to a `conflicted[]` array — CU-86caypwzm found this path is not
 * currently wired to any UI trigger (the availability grid always
 * multi-selects, even for one cell, so every real reservation attempt goes
 * through `batch-reserve`/this helper). It is kept, still unit-tested, in
 * case a future non-batch trigger is added; it is not migrated wholesale to
 * this helper's `showBatchReserveConflictToasts`/`TOAST_KEY_BY_REASON`
 * i18n-key mapping. It DOES reuse the shared `mapApiErrorCodeToReason` below
 * for the subset of codes both flows have in common, mapping the result to
 * its own toast keys/namespace afterward.
 */
import { toast } from '@ooh/ui/components/sonner';
import { ApiErrorCode } from '@ooh/contracts';

export type BatchReserveConflictReason =
  | 'reservationConflict'
  | 'staleState'
  | 'notFound'
  | 'forbidden'
  | 'validationError'
  | 'minWindowViolation'
  | 'generic';

const TOAST_KEY_BY_REASON: Record<BatchReserveConflictReason, string> = {
  // Kept as the pre-existing key name (unchanged message) for backward compat.
  reservationConflict: 'batchReserveConflict',
  staleState: 'batchReserveConflictStaleState',
  notFound: 'batchReserveConflictNotFound',
  forbidden: 'batchReserveConflictForbidden',
  validationError: 'batchReserveConflictValidationError',
  minWindowViolation: 'batchReserveConflictMinWindowViolation',
  generic: 'batchReserveConflictGeneric',
};

/**
 * Maps a raw `ApiErrorCode` string to a small, generic reason bucket. Shared
 * by `batchReserveConflictReason` (below) and
 * `offers/availability-shared/availability-reservation-actions.ts`'s
 * `errorCodeToToastKey` — the common subset of codes both flows handle
 * identically. Each caller still maps the returned reason to its OWN i18n
 * key/namespace; this function owns only the errorCode -> reason switch.
 */
export function mapApiErrorCodeToReason(errorCode: string): BatchReserveConflictReason {
  switch (errorCode) {
    case ApiErrorCode.RESERVATION_CONFLICT:
    case ApiErrorCode.CONFLICT:
      return 'reservationConflict';
    case ApiErrorCode.STALE_STATE:
      return 'staleState';
    case ApiErrorCode.NOT_FOUND:
      return 'notFound';
    case ApiErrorCode.FORBIDDEN:
      return 'forbidden';
    case ApiErrorCode.VALIDATION_ERROR:
      return 'validationError';
    case ApiErrorCode.MIN_WINDOW_VIOLATION:
      return 'minWindowViolation';
    default:
      return 'generic';
  }
}

/** Maps a single batch-reserve conflict cell's raw errorCode string to a toast reason. */
export function batchReserveConflictReason(errorCode: string): BatchReserveConflictReason {
  return mapApiErrorCodeToReason(errorCode);
}

type BatchReserveConflictCell = {
  errorCode: string;
  minWindowDays?: number;
  selectedDays?: number;
  // Present on every real `conflicted[]` entry (batch-reserve/lock-reservations); absent
  // on the synthetic per-entry object `describeOutcomeReason` (offers/header/
  // outcome-breakdown-columns.tsx) builds from a CampaignOutcomeItem to reuse
  // `describeBatchOutcomeFailure` below, which never needs them. Optional so both
  // callers satisfy this one shared type.
  mediaUnitId?: number;
  dateFrom?: Date;
  dateTo?: Date;
};

const ONE_DAY_MS = 86_400_000;

type LocatedBatchReserveConflictCell = BatchReserveConflictCell &
  Required<Pick<BatchReserveConflictCell, 'mediaUnitId' | 'dateFrom' | 'dateTo'>>;

function hasLocation(cell: BatchReserveConflictCell): cell is LocatedBatchReserveConflictCell {
  return cell.mediaUnitId != null && cell.dateFrom != null && cell.dateTo != null;
}

/**
 * True when `cells` all belong to one `mediaUnitId` and, sorted by `dateFrom`, form one
 * contiguous/overlapping span — mirrors the server's own adjacency rule
 * (`isAdjacentOrOverlapping` in apps/api/src/reservations/min-window-span.ts: a gap of at
 * most one day still counts as contiguous). A single cell is trivially one window. 2+
 * cells missing `mediaUnitId`/`dateFrom`/`dateTo` fail closed rather than assume they're
 * the same window.
 */
function isOneContiguousWindow(cells: BatchReserveConflictCell[]): boolean {
  if (cells.length <= 1) return true;
  if (!cells.every(hasLocation)) return false;
  const located = cells as LocatedBatchReserveConflictCell[];
  const [first, ...rest] = located;
  if (!first) return false;
  if (rest.some((cell) => cell.mediaUnitId !== first.mediaUnitId)) return false;
  const sorted = [...located].sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime());
  const [firstSorted, ...restSorted] = sorted;
  if (!firstSorted) return false;
  let currentEnd = firstSorted.dateTo.getTime();
  for (const cell of restSorted) {
    if (cell.dateFrom.getTime() > currentEnd + ONE_DAY_MS) return false;
    currentEnd = Math.max(currentEnd, cell.dateTo.getTime());
  }
  return true;
}

/**
 * The API coalesces a contiguous rejected date range into one logical window but still
 * emits one `conflicted[]` entry per day in that range, each carrying identical
 * `minWindowDays`/`selectedDays`. A shared pair alone isn't proof of that: min-window
 * settings are standardized per media type, so two genuinely separate rejected windows
 * (different media units, or non-contiguous ranges on the same unit) can coincidentally
 * share the same pair. Only treat the group as one coalesced window, and show the exact
 * message, when the day counts match AND `isOneContiguousWindow` confirms the cells are
 * actually one span; otherwise fall back to the generic message. Extracted to keep
 * `showBatchReserveConflictToasts` under the complexity lint threshold.
 */
function exactMinWindowCell(
  reason: BatchReserveConflictReason,
  cells: BatchReserveConflictCell[],
): { minWindowDays: number; selectedDays: number } | null {
  if (reason !== 'minWindowViolation') return null;
  const [first, ...rest] = cells;
  if (first?.minWindowDays == null || first.selectedDays == null) return null;
  const sameDayCounts = rest.every(
    (cell) =>
      cell.minWindowDays === first.minWindowDays && cell.selectedDays === first.selectedDays,
  );
  if (!sameDayCounts || !isOneContiguousWindow(cells)) return null;
  return { minWindowDays: first.minWindowDays, selectedDays: first.selectedDays };
}

/**
 * Maps a single `conflicted[]`/`failed[]` entry (batch-reserve, lock-reservations, or
 * the unified SELECT popover's own mixed-outcome result) to a localized, business-readable
 * explanation instead of the raw `errorCode` — the per-entry sibling of
 * `showBatchReserveConflictToasts`'s grouped toasts, reused so every one of this
 * product's "failed" result rows read identically (CU-86cb5z48p; `lock-reservations-dialog.tsx`'s
 * `lockOutcomeFailedDetail` is this function's independent per-domain twin, per the codebase's
 * existing "same shape, defined separately per entry point" convention).
 */
export function describeBatchOutcomeFailure(
  entry: BatchReserveConflictCell,
  t: (key: string, options?: Record<string, unknown>) => string,
  keyPrefix = 'toasts',
): string {
  if (entry.errorCode === ApiErrorCode.MIN_WINDOW_VIOLATION) {
    if (entry.minWindowDays != null && entry.selectedDays != null) {
      return t(`${keyPrefix}.batchReserveConflictMinWindowViolationExact`, {
        minWindowDays: entry.minWindowDays,
        selectedDays: entry.selectedDays,
        count: entry.minWindowDays,
      });
    }
    return t(`${keyPrefix}.batchReserveConflictMinWindowViolation`, { count: 1 });
  }
  const reason = batchReserveConflictReason(entry.errorCode);
  return t(`${keyPrefix}.${TOAST_KEY_BY_REASON[reason]}`, { count: 1 });
}

/**
 * Shows one `toast.error` per distinct conflict reason found in
 * `conflicted`, translated via `t('${keyPrefix}.${key}', { count })`.
 * No-op when `conflicted` is empty.
 *
 * Special case for `minWindowViolation` (CU-86caypwzm): the availability grid
 * always multi-selects, even for a single cell (there is no separate
 * non-batch reserve action in the UI), so this is the ONLY reachable path for
 * showing a client the exact required-vs-selected day count. See
 * `exactMinWindowCell` for when that exact message applies vs the generic
 * bucketed fallback.
 */
export function showBatchReserveConflictToasts(
  conflicted: BatchReserveConflictCell[],
  t: (key: string, options?: Record<string, unknown>) => string,
  keyPrefix = 'toasts',
): void {
  if (conflicted.length === 0) return;
  const groups = new Map<BatchReserveConflictReason, BatchReserveConflictCell[]>();
  for (const cell of conflicted) {
    const reason = batchReserveConflictReason(cell.errorCode);
    const group = groups.get(reason) ?? [];
    group.push(cell);
    groups.set(reason, group);
  }
  for (const [reason, cells] of groups) {
    const exact = exactMinWindowCell(reason, cells);
    if (exact) {
      // `count` drives i18next's _one/_few/_many/_other plural suffix selection on
      // minWindowDays (e.g. "1 dzień" vs "7 dni") — the key itself still interpolates
      // both `minWindowDays` and `selectedDays` from the same options object.
      toast.error(
        t(`${keyPrefix}.batchReserveConflictMinWindowViolationExact`, {
          ...exact,
          count: exact.minWindowDays,
        }),
      );
      continue;
    }
    toast.error(t(`${keyPrefix}.${TOAST_KEY_BY_REASON[reason]}`, { count: cells.length }));
  }
}
